import React from "react";
import { useScheduleConflictConfirm } from "../conflicts/useScheduleConflictConfirm";
import { CourseApi, courseschedulesApi } from "api";
import { ScheduleTypeSelectionForm } from "pages/CourseCreate/Schedule/create-new/ScheduleTypeSelectionForm";
import { ScheduleTopicFormSchema } from "pages/CourseCreate/Schedule/create-new/ScheduleTopicForm";
import { BaseScheduleFormSchema } from "pages/CourseCreate/Schedule/create-new/BaseScheduleForm";
import { ScheduleTopicGroupFormSchema } from "pages/CourseCreate/Schedule/create-new/ScheduleTopicGroupForm";

import { PaziaModal } from "components/molecules";
import { deFormatDate, toTimeFormat } from "utils";
import { useCourseSchedule } from "api/hooks";
import { ModalHeader } from "../../../../components/templates/Modals/ModalHeader";
import { CourseCreateActionTypes, useCourseCreate } from "pages/CourseCreate/CourseCreateContext";
import { useToaster } from "utils";
import { ScheduleModel } from "components/molecules/MonthCalendar";
import { useNavigate } from "react-router-dom";
import { routes } from "consts";
import { removeTrailingSlash } from "utils/urlUtils";
import { ExamScheduleReadOnlyPreview } from "./ExamScheduleReadOnlyPreview";
import { ScheduleFormTrainers } from "../BaseTrainersScheduleForm";

interface EditCourseScheduleModalProps {
  onCloseRequest: () => Promise<void>;
  course: CourseApi;
  courseSchedule?: ScheduleModel;
}

export const EditCourseScheduleModal = ({ onCloseRequest, course, courseSchedule }: EditCourseScheduleModalProps) => {
  return (
    <PaziaModal
      id="edit-session-modal"
      onCloseRequest={onCloseRequest}
      isOpen={!!courseSchedule}
      size="2xl"
      className="min-h-[50%] p-4"
    >
      <ModalHeader>Edycja wydarzenia</ModalHeader>
      {!!courseSchedule && (
        <EditCourseScheduleModalContent
          course={course}
          courseSchedule={courseSchedule}
          onCloseRequest={onCloseRequest}
        />
      )}
    </PaziaModal>
  );
};
const EditCourseScheduleModalContent = ({
  course,
  courseSchedule,
  onCloseRequest,
}: {
  course: CourseApi;
  courseSchedule: ScheduleModel;
  onCloseRequest: () => Promise<void>;
}) => {
  const { data, isLoading } = useCourseSchedule(course.data.id, courseSchedule.id);
  const { showError, showSuccess } = useToaster();
  const { dispatch } = useCourseCreate();
  const navigate = useNavigate();
  const { confirmSave, dialog: conflictDialog } = useScheduleConflictConfirm(Number(course.data.id));

  const onBreakSubmit = async (data: BaseScheduleFormSchema) => {
    try {
      const request = {
        day: deFormatDate(data.day)!,
        timeFrom: toTimeFormat(+data.fromTime),
        timeTo: toTimeFormat(+data.toTime),
        courseSessionId: Number(data.session),
      };
      await courseschedulesApi.updateBreak(Number(course.data.id), courseSchedule.id, request);
      dispatch({
        type: CourseCreateActionTypes.RefreshSchedule,
        payload: true,
      });
      showSuccess("Przerwa została zaktualizowana.");
      onCloseRequest();
    } catch (e) {
      console.error(e);
      showError("Nie udało się zapisać przerwy.");
    }
  };

  const onTopicSubmit = async (data: ScheduleTopicFormSchema) => {
    const request = {
      day: deFormatDate(data.day)!,
      timeFrom: toTimeFormat(+data.fromTime),
      timeTo: toTimeFormat(+data.toTime),
      courseSessionId: Number(data.session),
      placeId: Number(data.place),
      placeType: Number(data.placeType),
      topicId: Number(data.topic),
      trainers: ScheduleFormTrainers.toRequestTrainers(data.trainers),
    };
    const trainerIds = request.trainers.map((t) => t.trainerId);
    if (!(await confirmSave({ ...request, trainerIds, excludeScheduleId: courseSchedule.id }))) return false;
    try {
      await courseschedulesApi.updateTopic(Number(course.data.id), courseSchedule.id, request);
      dispatch({
        type: CourseCreateActionTypes.RefreshSchedule,
        payload: true,
      });
      showSuccess("Temat został zaktualizowany.");
      onCloseRequest();
    } catch (e) {
      console.error(e);
      showError("Nie udało się zapisać tematu.");
    }
  };

  const onTopicGroupSubmit = async (data: ScheduleTopicGroupFormSchema) => {
    const request = {
      day: deFormatDate(data.day)!,
      timeFrom: toTimeFormat(+data.fromTime),
      timeTo: toTimeFormat(+data.toTime),
      courseSessionId: Number(data.session),
      placeId: Number(data.place),
      placeType: Number(data.placeType),
      topicGroupId: Number(data.topicGroup),
      trainers: ScheduleFormTrainers.toRequestTrainers(data.trainers),
    };
    const trainerIds = request.trainers.map((t) => t.trainerId);
    if (!(await confirmSave({ ...request, trainerIds, excludeScheduleId: courseSchedule.id }))) return false;
    try {
      await courseschedulesApi.updateTopicGroup(Number(course.data.id), courseSchedule.id, request);
      dispatch({
        type: CourseCreateActionTypes.RefreshSchedule,
        payload: true,
      });
      showSuccess("Zajęcia zostały zaktualizowane.");
      onCloseRequest();
    } catch (e) {
      console.error(e);
      showError("Nie udało się zapisać grupy tematów.");
    }
  };

  if (!data) return null;

  if (data.schedule.examId) {
    return (
      <ExamScheduleReadOnlyPreview
        schedule={data.schedule}
        onNavigateToExams={() => {
          navigate(removeTrailingSlash(`${routes.COURSES_EDIT(course.data.id)}/${routes.COURSES_EDIT_EXAMS}`));
          onCloseRequest();
        }}
      />
    );
  }

  return (
    <div className="flex max-h-[80vh] min-h-[50vh] flex-col overflow-y-auto">
      <ScheduleTypeSelectionForm
        course={course}
        schedule={data}
        formStyle={{}}
        onBreakSubmit={onBreakSubmit}
        onTopicSubmit={onTopicSubmit}
        onTopicGroupSubmit={onTopicGroupSubmit}
      />
      {conflictDialog}
    </div>
  );
};
